import { NumberInputField, ValidationErrorData } from './common';
import { CampaignPlatform } from './domain';
import {
    GdCampaignBudgetPeriod,
    GdCampaignBudgetShowMode
} from './graphql-grid';

export enum StrategyParamNames {
    goal = 'goal',
    bid = 'bid',
    avgBid = 'avgBid',
    filterAvgBid = 'filterAvgBid',
    avgCpa = 'avgCpa',
    filterAvgCpa = 'filterAvgCpa',
    sum = 'sum',
    clicksLimit = 'clicksLimit',
    roiCoef = 'roiCoef',
    profitability = 'profitability',
    reserveReturn = 'reserveReturn',
    avgCpi = 'avgCpi',
    avgCpm = 'avgCpm',
    separateBidding = 'separateBidding'
}
export enum OptimizationStrategyParamNames {
    avgBidOpt = 'avgBidOpt',
    avgCpaOpt = 'avgCpaOpt'
}

export enum StrategyBudgetParamNames {
    sum = 'sum',
    showMode = 'showMode',
    period = 'period',
    autoProlongation = 'autoProlongation',
    start = 'start',
    finish = 'finish'
}

export interface CampaignStrategyGoal {
    name: string;
    id?: number;
}

export enum AttributionModel {
    LAST_SIGNIFICANT_CLICK = 'last_significant_click',
    LAST_CLICK = 'last_click',
    FIRST_CLICK = 'first_click'
}

export interface LogCampaignDayBudget {
    [StrategyBudgetParamNames.sum]: number;
    [StrategyBudgetParamNames.showMode]: GdCampaignBudgetShowMode;
    [StrategyBudgetParamNames.period]: GdCampaignBudgetPeriod;
    [StrategyBudgetParamNames.autoProlongation]: boolean;
    [StrategyBudgetParamNames.start]: string;
    [StrategyBudgetParamNames.finish]: string;
}

export interface LogCampaignStrategyParams {
    [StrategyParamNames.goal]?: CampaignStrategyGoal;
    [StrategyParamNames.bid]?: number;
    [StrategyParamNames.avgBid]?: number;
    [StrategyParamNames.filterAvgBid]?: number;
    [StrategyParamNames.avgCpa]?: number;
    [StrategyParamNames.filterAvgCpa]?: number;
    [StrategyParamNames.sum]?: number;
    [StrategyParamNames.clicksLimit]?: number;
    [StrategyParamNames.profitability]?: number;
    [StrategyParamNames.roiCoef]?: number;
    [StrategyParamNames.reserveReturn]?: number;
    [StrategyParamNames.avgCpi]?: number;
    [StrategyParamNames.avgCpm]?: number;
    [StrategyParamNames.separateBidding]?: boolean;
}

export type LogCampaignStrategyParamValue =
    | number
    | boolean
    | string
    | GdCampaignBudgetPeriod
    | GdCampaignBudgetShowMode
    | CampaignStrategyGoal;

export enum DnaStrategyType {
    OPTIMIZE_CLICKS = 'OPTIMIZE_CLICKS',
    OPTIMIZE_CPA = 'OPTIMIZE_CPA',
    ROI = 'ROI',
    MANUAL = 'MANUAL'
}

export enum StrategyErrorType {
    avgClickPrice = 'avgClickPrice',
    weeklyBudget = 'weeklyBudget',
    weekBundle = 'weekBundle',
    maxClickPrice = 'maxClickPrice',
    goalId = 'goalId',
    avgCpaPrice = 'avgCpaPrice',
    roiCoef = 'roiCoef',
    profitability = 'profitability',
    dailyBudget = 'dailyBudget',
    optimizationParams = 'optimizationParams',
    auxiliaryParams = 'auxiliaryParams',
    dailyBudgetParams = 'dailyBudgetParams',
    common = 'common'
}

export type StrategyEditorValidationResult = Partial<
    Record<StrategyErrorType, ValidationErrorData[]>
>;

export interface StrategyBase {
    type: DnaStrategyType;
    platform: CampaignPlatform;
    attributionModel: AttributionModel;
    errors: StrategyEditorValidationResult;
}

export enum OptimizationCriteria {
    AVG_CLICK = 'AVG_CLICK',
    WEEKLY_BUDGET = 'WEEKLY_BUDGET',
    WEEK_BUNDLE = 'WEEK_BUNDLE'
}

export interface StrategyOptimizeClicks extends StrategyBase {
    type: DnaStrategyType.OPTIMIZE_CLICKS;
    optimizationCriteria: OptimizationCriteria;
    avgClickPrice?: number;
    avgClickPriceEnabled: boolean;
    weeklyBudget?: number;
    weeklyBudgetEnabled: boolean;
    weekBundle?: number;
    maxClickPrice?: number;
}

export interface StrategyForEditorOptimizeClicks extends StrategyBase {
    type: DnaStrategyType.OPTIMIZE_CLICKS;
    optimizationCriteria: OptimizationCriteria;
    avgClickPrice: NumberInputField;
    avgClickPriceEnabled: boolean;
    weeklyBudget: NumberInputField;
    weeklyBudgetEnabled: boolean;
    weekBundle: NumberInputField;
    maxClickPrice: NumberInputField;
}

export interface StrategyOptimizeCpa extends StrategyBase {
    type: DnaStrategyType.OPTIMIZE_CPA;
    goalId: string;
    avgCpaPrice?: number;
    weeklyBudget?: number;
    maxClickPrice?: number;
}

export interface StrategyForEditorOptimizeCpa extends StrategyBase {
    type: DnaStrategyType.OPTIMIZE_CPA;
    goalId: string;
    avgCpaPrice: NumberInputField;
    weeklyBudget: NumberInputField;
    maxClickPrice: NumberInputField;
}

export interface StrategyRoi extends StrategyBase {
    type: DnaStrategyType.ROI;
    roiCoef: number;
    goalId: string;
    profitability: number;
    reserveReturn: number;
    weeklyBudget?: number;
    maxClickPrice?: number;
}

export interface StrategyForEditorRoi extends StrategyBase {
    type: DnaStrategyType.ROI;
    roiCoef: NumberInputField;
    goalId: string;
    profitability: NumberInputField;
    reserveReturn: number;
    weeklyBudget: NumberInputField;
    maxClickPrice: NumberInputField;
}

export interface StrategyManual extends StrategyBase {
    type: DnaStrategyType.MANUAL;
    separateBidding: boolean;
    dailyBudgetMode: GdCampaignBudgetShowMode;
    dailyBudget?: number;
    contextLimit: number;
    cpcHoldEnabled: boolean;
}

export interface StrategyForEditorManual extends StrategyBase {
    type: DnaStrategyType.MANUAL;
    separateBidding: boolean;
    dailyBudgetMode: GdCampaignBudgetShowMode;
    dailyBudget: NumberInputField;
    contextLimit: number;
    cpcHoldEnabled: boolean;
}

export type Strategy =
    | StrategyOptimizeClicks
    | StrategyOptimizeCpa
    | StrategyRoi
    | StrategyManual;

export type StrategyForEditor =
    | StrategyForEditorOptimizeClicks
    | StrategyForEditorOptimizeCpa
    | StrategyForEditorRoi
    | StrategyForEditorManual;

export interface StrategyEditorInitData {
    editorId: string;
    strategy: StrategyForEditor;
}
